import { Card, Box, IconButton, Tooltip, Typography, Button } from "@mui/material"
import { ListContextProvider, useDeleteWithConfirmController, useListContext, useRefresh } from "ra-core"
import { FC, useCallback, useRef, useState } from "react"
import { Content } from "@/components/content/Content"
import { FunctionField, TextField } from "@/components/field"
import { Datagrid } from "@/components/list/datagrid"
import Scrollbar from "@/components/Scrollbar"
import { RecPatch } from "@/dataProvider/tools"
import { ReasonCollectResponseDto } from "@/gen/api/governance"
import { OdResourcePageProps } from "@/types/resource"
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import AddIcon from '@mui/icons-material/Add';
import EditIcon from "@mui/icons-material/Edit"
import useLocalListController from "@/components/list/useLocalListController"
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import Confirm from "@/components/widgets/Confirm"
import CreateOrEdit from "@/controller/CreateOrEdit"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import EditReasonCollect from "@/components/governance/EditReasonCollect"


const reasonCollectListOptions : {sorters: Record<string, (a:ReasonCollectResponseDto & RecPatch, b:ReasonCollectResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => (a.reasonText || '') < (b.reasonText || '') ? -1 : 1,
  }
}

export const ReasonCollect: FC<OdResourcePageProps> = (props) => {
  const { resource } = props
  const listCtrl = useLocalListController({ resource, basePath: '', filter: { } }, reasonCollectListOptions)

  return (
    <ListContextProvider value={listCtrl}>
      <ReasonCollectBody {...props} />
    </ListContextProvider>
  )
}

const ReasonCollectBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const listCtx = useListContext<ReasonCollectResponseDto & RecPatch>()
  
  const [openEdit, setOpenEdit] = useState(false)
  const [recordToEdit, setRecordToEdit] = useState<ReasonCollectResponseDto | null>(null)

  const refresh = useRefresh()
  const refreshLight = useCallback(() => refresh(), [refresh])
  const savedHandlers = useDefaultSavedHandlers(() => `${recordToEdit ? 'Edit' : 'Create'} Reason for Collect`)

  const recordToDel = useRef<ReasonCollectResponseDto&RecPatch | null>(null)
  const delHandlers = useDefaultSavedHandlers('Delete Reason for Collect')
  delHandlers.onSuccess.merge(refreshLight)
  const {
    open: openConfirm,
    loading,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    record: recordToDel.current as any,
    ...delHandlers,
  })

  return (
    <Content 
      pageName="Reasons for Collect" 
      resourceName="Reasons for Collect" 
      breadcrumbs={options?.breadcrumbs}
      mainActions={
        <Button
          color="primary"
          variant="contained"
          startIcon={
            <AddIcon />
          }
          onClick={() => {
            setRecordToEdit(null)
            setOpenEdit(true)
          }}
        >
          Add Reason for Collect
        </Button>
      }
    >
    
      <Card>
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
              }}
              {...listCtx}
            >
              <TextField source="reasonText" label="Reason for Collect" sortable={false} />
              <FunctionField 
                textAlign="center" 
                label="Action" 
                sortable={false} 
                render={(d?:ReasonCollectResponseDto&RecPatch) => (
                  d &&
                  <>
                    <Tooltip title="Edit">
                      <IconButton
                        sx={{
                          color: 'text.primary' as any,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => {
                          setRecordToEdit(d || null)
                          setOpenEdit(true)
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        sx={{
                          color: 'text.primary' as any,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={(e:any) => {
                          recordToDel.current = d as any
                          handleDialogOpen(e)
                        }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      <CreateOrEdit resource={resource} basePath="" record={recordToEdit} onFailure={savedHandlers.onFailure} onSuccess={savedHandlers.onSuccess.merge(refreshLight)}>
        {openEdit && <EditReasonCollect open={openEdit} setOpen={(x:boolean) => {setOpenEdit(x); if (!x) { setTimeout(() => setRecordToEdit(null)) }}} />}
      </CreateOrEdit>
      <Confirm
        isOpen={openConfirm}
        title={"Delete Reason for Collect"}
        loading={loading}
        onConfirm={handleDelete}
        onClose={(e:any) => {handleDialogClose(e); recordToDel.current = null}}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to proceed with the deletion of this
                {(recordToDel.current?.reasonText) ? ` "${recordToDel.current?.reasonText}" ` : " "}
                Reason for Collect.
              </Typography>
            </Box>
          </Box>
        )}
      />
    </Content>
  )
}

